import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Typography,
  MenuItem,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getCategories,
  fetchItemsByCategoryAndSubcategory,
  GetApprovedSuppliers as fetchSuppliers,
  getUnits,
  CreatePurchaseVoucher,
  UpdatePurchaseVoucher,
  fetchpos,
  GetNextPvNo,
  getSizes,
} from "../../../Services/Services";
import Autocomplete from "@mui/material/Autocomplete";
import { toast, ToastContainer } from "react-toastify";
import { debounce } from "lodash";

export default function Purchaseentryform() {
  const stored = localStorage.getItem("selectedDivision");
  const division = stored ? JSON.parse(stored) : null;

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const disabledBg = isDark
    ? alpha(theme.palette.common.white, 0.06)
    : "#f5f5f5";
  const inputNativeBorder = isDark ? "#555" : "#c4c4c4";
  const inputNativeBg = isDark
    ? alpha(theme.palette.common.white, 0.04)
    : "#fcfcfc";
  const inputNativeColor = isDark ? "#fff" : "inherit";
  const tableHeadBg = isDark
    ? alpha(theme.palette.primary.main, 0.15)
    : "#f0f4ff";

  const nativeInputStyle = (hasError = false) => ({
    height: "36px",
    padding: "8px",
    fontSize: "0.875rem",
    borderRadius: "4px",
    backgroundColor: inputNativeBg,
    border: hasError ? "1px solid red" : `1px solid ${inputNativeBorder}`,
    color: inputNativeColor,
    outline: "none",
  });

  const emptyPvItem = {
    category: null,
    sub_category: null,
    item: null,
    description: "",
    quantity: 0,
    used_qty: 0,
    unit: "",
    unit_name: "",
    rate: "",
    amount: "",
    size: null,
    gst_percentage: "",
    gst_amount: "0.00",
  };

  const defaultTermsList = [
    { key: "Delivery Period", value: "Ready stock", is_default: true },
    { key: "Warranty", value: "12 months from delivery", is_default: true },
    { key: "Freight", value: "Ex works. Raipur", is_default: true },
    { key: "Inspection", value: "By buyer at site", is_default: true },
    { key: "Quality", value: "As per specifications", is_default: true },
    {
      key: "Rejection/Claim",
      value: "Allowed within 7 days",
      is_default: true,
    },
    { key: "Jurisdiction", value: "Raipur", is_default: true },
    {
      key: "Payment Terms",
      value: "Material received after 30 days",
      is_default: true,
    },
    { key: "GST", value: "18% applicable", is_default: true },
    { key: "Billing gst no-", value: division?.gst_no || "", is_default: true },
  ];

  // ── States ──
  const [formData, setFormData] = useState({
    voucher_no: "",
    invoice_no: "",
    invoice_date: "",
    lr_no: "",
    grn_no: "",
    purchase_order: "",
    vehicle_no: "",
    payment_status: "",
    supplier: "",
    division: division?.division_id || "",
    project: "",
    billing_name: "",
    billing_address: "",
    billing_pan: "",
    billing_gst: "",
    billing_division: division?.division_id || "",
    billing_email: "",
    billing_contact: "",
    billed_to_name:
      "Siddhart Industries MATERIAL HANDLING EQUIPMENTS PVT. LTD.",
    billed_to_email: "info@Siddhart Industriesmhe.com",
    billed_to_contact: "9825586999",
    billed_to_state_name: "Chattisgarh",
    billed_to_state_code: "22",
    billed_to_gst_no: "22AAECC0064F1ZC",
    billed_to_pan_no: "AAECC0064F",
    billed_to_address:
      "Shed No.6, Industrial Growth Center, Borai, Rasamada, Chhattisgarh 491001",
    shipping_address: "",
    shipping_contact: "",
    shipping_phone: "",
    narration: "",
    total_amount: "0.00",
    gst_amount: "0.00",
    other_expense: "0.00",
    final_amount: "0.00",
    items: [{ ...emptyPvItem }],
    created_by: localStorage.getItem("user_id"),
  });

  const navigate = useNavigate();
  const location = useLocation();

  const [itemOptions, setItemOptions] = useState({});
  const [unitOptions, setUnitOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [rowCategoryOptions, setRowCategoryOptions] = useState({});
  const [includeShipping, setIncludeShipping] = useState(false);
  const [terms, setTerms] = useState(defaultTermsList);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [submitErrors, setSubmitErrors] = useState({});
  const [itemFieldErrors, setItemFieldErrors] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState("");

  const locationState = location.state || {};
  const issueData = locationState.issue || null;
  const poDataFromState = locationState.poData || null;
  const poData = poDataFromState || issueData;
  const isEdit = locationState.isEdit || !!issueData;

  // ── Calculation Helpers ──
  const calculateItemGST = (amount, gstPercent) =>
    (((parseFloat(amount) || 0) * (parseFloat(gstPercent) || 0)) / 100).toFixed(
      2,
    );

  const recalcMainTotals = (items, otherExpense) => {
    let totalAmount = 0;
    let totalGST = 0;
    items.forEach((item) => {
      totalAmount += parseFloat(item.amount) || 0;
      totalGST += parseFloat(item.gst_amount) || 0;
    });
    const finalAmount =
      totalAmount + totalGST + (parseFloat(otherExpense) || 0);
    return {
      total_amount: totalAmount.toFixed(2),
      gst_amount: totalGST.toFixed(2),
      final_amount: finalAmount.toFixed(2),
    };
  };

  const updateTotals = (
    updatedItems,
    otherExpense = formData.other_expense,
  ) => {
    const totals = recalcMainTotals(updatedItems, otherExpense);
    setFormData((prev) => ({
      ...prev,
      total_amount: totals.total_amount,
      gst_amount: totals.gst_amount,
      final_amount: totals.final_amount,
      items: updatedItems,
    }));
  };

  // ── Item Row Handlers ──
  const addItemRow = () => {
    const updatedItems = [...formData.items, { ...emptyPvItem }];
    updateTotals(updatedItems, formData.other_expense);
  };

  const deleteItemRow = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    updateTotals(updatedItems, formData.other_expense);
    if (itemFieldErrors[index]) {
      const newItemErrors = [...itemFieldErrors];
      delete newItemErrors[index];
      setItemFieldErrors(newItemErrors);
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    const row = { ...updatedItems[index] };

    row[field] = value;

    if (field === "quantity" || field === "rate") {
      const qty = parseFloat(field === "quantity" ? value : row.quantity) || 0;
      const rate = parseFloat(field === "rate" ? value : row.rate) || 0;
      row.amount = (qty * rate).toFixed(2);
    }

    if (field === "amount" || field === "gst_percentage") {
      row.gst_amount = calculateItemGST(row.amount, row.gst_percentage);
    }

    updatedItems[index] = row;
    updateTotals(updatedItems, formData.other_expense);

    if (itemFieldErrors[index] && itemFieldErrors[index][field]) {
      const newItemErrors = [...itemFieldErrors];
      delete newItemErrors[index][field];
      if (Object.keys(newItemErrors[index]).length === 0)
        delete newItemErrors[index];
      setItemFieldErrors(newItemErrors);
    }
  };

  const handleOtherExpenseChange = (value) => {
    const otherExp = value || "0.00";
    const totals = recalcMainTotals(formData.items, otherExp);
    setFormData((prev) => ({
      ...prev,
      other_expense: otherExp,
      ...totals,
    }));
  };

  // ── Field Handlers ──
  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (submitErrors[field]) {
      setSubmitErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTermChange = (index, field, value) => {
    const updatedTerms = [...terms];
    updatedTerms[index][field] = value;
    setTerms(updatedTerms);
  };

  const removeTerm = (index) => {
    setTerms(terms.filter((_, i) => i !== index));
  };

  // ── Supplier Fetch ──
  const fetchSuppliersList = async (page = 1, filters = {}) => {
    try {
      setSupplierLoading(true);
      const response = await fetchSuppliers(page, filters);
      const results = Array.isArray(response.results)
        ? response.results
        : response || [];
      if (page === 1) setSuppliers(results);
      else setSuppliers((prev) => [...prev, ...results]);
    } catch {
      toast.error("Failed to load supplier list");
    } finally {
      setSupplierLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(
      () => fetchSuppliersList(1, { name: supplierSearch }),
      500,
    );
    return () => clearTimeout(t);
  }, [supplierSearch]);

  // ── Initial Data Fetch ──
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [unitRes, sizeRes] = await Promise.all([getUnits(), getSizes()]);
        setUnitOptions(
          Array.isArray(unitRes) ? unitRes : unitRes?.results || [],
        );
        setSizeOptions(
          Array.isArray(sizeRes) ? sizeRes : sizeRes?.results || [],
        );
      } catch {}
    };

    if (isEdit && poData) {
      const mappedItems = (poData.items || []).map((it) => ({
        ...it,
        category: it.category
          ? { id: it.category, name: it.category_name || "" }
          : null,
        sub_category: it.sub_category
          ? { subcat_id: it.sub_category, name: it.sub_category_name || "" }
          : null,
        item: it.item ? { id: it.item, name: it.item_name || "" } : null,
        unit: it.unit ?? it.uom ?? "",
        unit_name: it.unit_name ?? it.uom_name ?? "",
        description: it.description ?? it.item_name ?? "",
        rate: parseFloat(it.rate) || 0,
        quantity: parseFloat(it.quantity) || 0,
        amount: (parseFloat(it.rate) || 0) * (parseFloat(it.quantity) || 0),
        gst_percentage: it.gst_percentage || "",
        gst_amount: it.gst_amount || "0.00",
      }));

      const totals = recalcMainTotals(
        mappedItems,
        poData.other_expense || "0.00",
      );

      setFormData((prev) => ({
        ...prev,
        ...poData,
        voucher_no: poData.voucher_no || poData.purchase_number || "",
        purchase_order: poData.purchase_order || "",
        vehicle_no: poData.vehicle_no || "",
        created_by: localStorage.getItem("user_id"),
        billing_email: poData.billing_email || "",
        billing_contact: poData.billing_contact || "",
        billed_to_name:
          poData.billed_to_name || "Siddhart Industries MHE PVT. LTD.",
        billed_to_email:
          poData.billed_to_email || "info@Siddhart Industriesmhe.com",
        billed_to_contact: poData.billed_to_contact || "9302122967",
        billed_to_state_name: poData.billed_to_state_name || "Chattisgarh",
        billed_to_state_code: poData.billed_to_state_code || "22",
        billed_to_gst_no: poData.billed_to_gst_no || division?.gst_no || "",
        billed_to_pan_no: poData.billed_to_pan_no || division?.pan_no || "",
        billed_to_address: poData.billed_to_address || division?.address || "",
        narration: poData.narration || "",
        other_expense: poData.other_expense || "0.00",
        items: mappedItems,
        ...totals,
      }));

      setIncludeShipping(!!poData.shipping_address);
      setTerms(poData.terms_conditions || defaultTermsList);
    }

    fetchDropdowns();

    if (!isEdit && !sessionStorage.getItem("pvDraft")) {
      fetchNextVoucherNo();
    }
  }, []);

  // ── Edit mode - preload row options ──
  useEffect(() => {
    if (!isEdit) return;
    const rows = formData.items || [];
    if (!rows.length) return;
    let cancel = false;

    (async () => {
      const itemsMap = {};
      const catOptionsMap = {};

      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        const sizeId =
          r?.size?.id || (typeof r?.size === "number" ? r.size : null);

        if (sizeId) {
          try {
            const catData = await getCategories(1, "", sizeId);
            catOptionsMap[i] = Array.isArray(catData)
              ? catData
              : catData?.results || [];
          } catch {
            catOptionsMap[i] = [];
          }
        }

        if (r?.category?.id) {
          const itemData = await fetchItemsByCategoryAndSubcategory(
            r.category.id,
            0,
          );
          itemsMap[i] = (itemData?.results || []).map((x) => ({
            id: x.id,
            name: x.name,
            unit_id: x.unit || null,
            unit_name: x.unit_name || "",
            size_id: x.size || null,
            rate: x.rate || 0,
            description: x.description || "",
          }));
        }
      }

      if (!cancel) {
        setRowCategoryOptions(catOptionsMap);
        setItemOptions(itemsMap);
      }
    })();

    return () => {
      cancel = true;
    };
  }, [isEdit]);

  const fetchNextVoucherNo = async () => {
    try {
      const data = await GetNextPvNo();
      if (data?.purchase_voucher_no || data?.purchase_number) {
        const no = data.purchase_voucher_no || data.purchase_number;
        setFormData((prev) => ({
          ...prev,
          voucher_no: no,
          purchase_number: no,
        }));
      }
    } catch (error) {
      console.error("Error fetching voucher no:", error);
    }
  };

  // ── Row autocomplete handlers ──
  const handleInputChange = async (index, key, value) => {
    const updatedRows = [...formData.items];
    let row = { ...updatedRows[index] };

    if (itemFieldErrors[index] && itemFieldErrors[index][key]) {
      const newItemErrors = [...itemFieldErrors];
      delete newItemErrors[index][key];
      if (Object.keys(newItemErrors[index]).length === 0)
        delete newItemErrors[index];
      setItemFieldErrors(newItemErrors);
    }

    switch (key) {
      case "size":
        row = {
          ...row,
          size: value,
          category: null,
          item: null,
          description: "",
          unit: "",
          unit_name: "",
          rate: "",
          amount: "",
          gst_percentage: "",
          gst_amount: "0.00",
        };
        if (value?.id) {
          try {
            const catData = await getCategories(1, "", value.id);
            const cats = Array.isArray(catData)
              ? catData
              : catData?.results || [];
            setRowCategoryOptions((prev) => ({ ...prev, [index]: cats }));
          } catch {
            setRowCategoryOptions((prev) => ({ ...prev, [index]: [] }));
          }
        } else {
          setRowCategoryOptions((prev) => ({ ...prev, [index]: [] }));
        }
        setItemOptions((prev) => ({ ...prev, [index]: [] }));
        break;

      case "category":
        row = {
          ...row,
          category: value,
          sub_category: null,
          item: null,
          description: "",
          unit: "",
          unit_name: "",
          rate: "",
          amount: "",
          gst_percentage: "",
          gst_amount: "0.00",
        };
        if (value?.id) {
          const itemData = await fetchItemsByCategoryAndSubcategory(
            value.id,
            0,
            1,
            "",
          );
          const mappedItems = (itemData.results || []).map((item) => ({
            id: item.id,
            name: item.name,
            unit_id: item.unit || null,
            unit_name: item.unit_name || "",
            size_id: item.size || null,
            rate: item.rate || 0,
            description: item.description || "",
          }));
          setItemOptions((prev) => ({ ...prev, [index]: mappedItems }));
        } else {
          setItemOptions((prev) => ({ ...prev, [index]: [] }));
        }
        break;

      case "item":
        if (value) {
          const unitObj = value?.unit_id
            ? unitOptions.find((u) => String(u.id) === String(value.unit_id))
            : null;
          const sizeObj = value?.size_id
            ? sizeOptions.find((s) => String(s.id) === String(value.size_id)) ||
              null
            : null;

          row = {
            ...row,
            item: value,
            description: value?.description || value?.name || "",
            rate: value?.rate || value?.price || "",
            unit: unitObj?.id || value?.unit_id || "",
            unit_name: unitObj?.name || value?.unit_name || "",
            size: sizeObj || row.size,
          };
          const qty = parseFloat(row.quantity) || 0;
          const rate = parseFloat(row.rate) || 0;
          row.amount = (qty * rate).toFixed(2);
          row.gst_amount = calculateItemGST(row.amount, row.gst_percentage);
        } else {
          row = {
            ...row,
            item: null,
            description: "",
            rate: "",
            amount: "",
            gst_amount: "0.00",
          };
        }
        break;

      case "unit":
        if (value && typeof value === "object") {
          row = {
            ...row,
            unit: value.id || "",
            unit_name: value.name || value.unit_name || "",
          };
        } else {
          row = { ...row, unit: value || "", unit_name: "" };
        }
        break;

      case "quantity":
      case "rate": {
        row = { ...row, [key]: value };
        const newQty =
          parseFloat(key === "quantity" ? value : row.quantity) || 0;
        const newRate = parseFloat(key === "rate" ? value : row.rate) || 0;
        row.amount = (newQty * newRate).toFixed(2);
        row.gst_amount = calculateItemGST(row.amount, row.gst_percentage);
        break;
      }

      case "gst_percentage":
        row = { ...row, gst_percentage: value };
        row.gst_amount = calculateItemGST(row.amount, row.gst_percentage);
        break;

      default:
        row = { ...row, [key]: value };
    }

    updatedRows[index] = row;
    updateTotals(updatedRows, formData.other_expense);
  };

  const handleItemInputChange = useCallback(
    debounce(async (index, row, newValue, type) => {
      if (type === "category") {
        const productTypeId = row.size?.id || null;
        if (!productTypeId) {
          setRowCategoryOptions((prev) => ({ ...prev, [index]: [] }));
          return;
        }
        const catData = await getCategories(
          1,
          newValue?.trim() || "",
          productTypeId,
        );
        const list = Array.isArray(catData?.results)
          ? catData.results
          : catData;
        setRowCategoryOptions((prev) => ({ ...prev, [index]: list || [] }));
        return;
      }

      if (type === "item" && row.category) {
        const itemData = await fetchItemsByCategoryAndSubcategory(
          row.category.id,
          0,
          1,
          newValue || "",
        );
        const mappedItems = (itemData.results || []).map((item) => ({
          id: item.id,
          name: item.name,
          unit_id: item.unit || null,
          unit_name: item.unit_name || "",
          size_id: item.size || null,
          rate: item.rate || 0,
          description: item.description || "",
        }));
        setItemOptions((prev) => ({ ...prev, [index]: mappedItems }));
        return;
      }

      if (type === "size") {
        const sizeData = await getSizes(newValue || "");
        const list = Array.isArray(sizeData?.results)
          ? sizeData.results
          : sizeData;
        setSizeOptions(list || []);
      }
    }, 500),
    [],
  );

  // ── Validation ──
  const validateForm = () => {
    const newErrors = {};
    const newItemErrors = [];

    if (!formData.voucher_no) newErrors.voucher_no = "Voucher No is required";
    if (!formData.supplier) newErrors.supplier = "Supplier is required";

    if (formData.billing_email) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formData.billing_email))
        newErrors.billing_email = "Invalid email format";
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    const internationalRegex = /^\+\d{10,14}$/;

    if (formData.billing_contact) {
      const cleanPhone = formData.billing_contact.replace(/[\s-]/g, "");
      if (!phoneRegex.test(cleanPhone) && !internationalRegex.test(cleanPhone))
        newErrors.billing_contact = "Invalid phone number format";
    }

    if (formData.billed_to_contact) {
      const cleanPhone = formData.billed_to_contact.replace(/[\s-]/g, "");
      if (!phoneRegex.test(cleanPhone) && !internationalRegex.test(cleanPhone))
        newErrors.billed_to_contact = "Invalid phone number format";
    }

    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
    const gstRegex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

    if (
      formData.billing_pan &&
      !panRegex.test(formData.billing_pan.toUpperCase())
    )
      newErrors.billing_pan = "Invalid PAN format";

    if (
      formData.billing_gst &&
      !gstRegex.test(formData.billing_gst.toUpperCase())
    )
      newErrors.billing_gst = "Invalid GSTIN format";

    if (
      formData.billed_to_pan_no &&
      !panRegex.test(formData.billed_to_pan_no.toUpperCase())
    )
      newErrors.billed_to_pan_no = "Invalid PAN format";

    if (
      formData.billed_to_gst_no &&
      !gstRegex.test(formData.billed_to_gst_no.toUpperCase())
    )
      newErrors.billed_to_gst_no = "Invalid GSTIN format";

    if (includeShipping && formData.shipping_phone) {
      const cleanPhone = formData.shipping_phone.replace(/[\s-]/g, "");
      if (!phoneRegex.test(cleanPhone) && !internationalRegex.test(cleanPhone))
        newErrors.shipping_phone = "Invalid phone number format";
    }

    if (!formData.items.length) {
      newErrors.items = "At least one item is required";
    } else {
      formData.items.forEach((item, index) => {
        const itemError = {};
        let hasError = false;

        if (!item.category) {
          itemError.category = "Category is required";
          hasError = true;
        }
        if (!item.item) {
          itemError.item = "Item is required";
          hasError = true;
        }
        if (!item.quantity || parseFloat(item.quantity) <= 0) {
          itemError.quantity = "Valid quantity is required";
          hasError = true;
        }
        if (!item.unit) {
          itemError.unit = "Unit is required";
          hasError = true;
        }
        if (!item.rate || parseFloat(item.rate) < 0) {
          itemError.rate = "Valid rate is required";
          hasError = true;
        }

        if (hasError) newItemErrors[index] = itemError;
      });
    }

    return { fieldErrors: newErrors, itemErrors: newItemErrors };
  };

  // ── Submit ──
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isSubmitting) {
      toast.warn("Please wait, form is already submitting...");
      return;
    }

    setSubmitErrors({});
    setItemFieldErrors([]);

    const validation = validateForm();
    if (
      Object.keys(validation.fieldErrors).length > 0 ||
      validation.itemErrors.length > 0
    ) {
      setSubmitErrors(validation.fieldErrors);
      setItemFieldErrors(validation.itemErrors);
      toast.error("Please fix all validation errors before submitting");
      return;
    }

    setIsSubmitting(true);

    try {
      const filteredTerms = terms.filter(
        (term) => term.key?.trim() !== "" && term.value?.trim() !== "",
      );

      const cleanedItems = formData.items.map((item) => ({
        category: item.category?.id || item.category || null,
        sub_category: item.sub_category?.subcat_id || item.sub_category || null,
        item: item.item?.id || item.item || null,
        size:
          typeof item.size === "object"
            ? item.size?.id || null
            : item.size || null,
        unit:
          typeof item.unit === "object"
            ? item.unit?.id || null
            : item.unit || null,
        description: item.description || "",
        quantity: parseFloat(item.quantity) || 0,
        received_qty: parseFloat(item.quantity) || 0,
        rate: parseFloat(item.rate) || 0,
        amount: parseFloat(item.amount) || 0,
        gst_percentage: parseFloat(item.gst_percentage) || 0,
        gst_amount: parseFloat(item.gst_amount) || 0,
        item_total:
          (parseFloat(item.amount) || 0) + (parseFloat(item.gst_amount) || 0),
      }));

      const fd = new FormData();
      fd.append("purchase_voucher_no", formData.voucher_no || "");
      fd.append("purchase_number", formData.voucher_no || "");
      fd.append("invoice_number", formData.invoice_no || "");
      fd.append("invoice_date", formData.invoice_date || "");
      fd.append("lr_no", formData.lr_no || "");
      fd.append("grn_no", formData.grn_no || "");
      fd.append("purchase_order", formData.purchase_order || "");
      fd.append("vehicle_no", formData.vehicle_no || "");
      fd.append("payment_status", formData.payment_status || "Unpaid");
      fd.append("supplier", formData.supplier || "");
      fd.append("project", formData.project || "");
      fd.append("billing_name", formData.billing_name || "");
      fd.append("billing_address", formData.billing_address || "");
      fd.append("billing_pan", formData.billing_pan || "");
      fd.append("billing_gst", formData.billing_gst || "");
      fd.append("billing_email", formData.billing_email || "");
      fd.append("billing_contact", formData.billing_contact || "");
      fd.append("billed_to_name", formData.billed_to_name || "");
      fd.append("billed_to_email", formData.billed_to_email || "");
      fd.append("billed_to_contact", formData.billed_to_contact || "");
      fd.append("billed_to_address", formData.billed_to_address || "");
      fd.append("billed_to_state_name", formData.billed_to_state_name || "");
      fd.append("billed_to_state_code", formData.billed_to_state_code || "");
      fd.append("billed_to_gst_no", formData.billed_to_gst_no || "");
      fd.append("billed_to_pan_no", formData.billed_to_pan_no || "");
      fd.append("include_shipping", includeShipping);
      fd.append("shipping_address", formData.shipping_address || "");
      fd.append("shipping_contact", formData.shipping_contact || "");
      fd.append("shipping_phone", formData.shipping_phone || "");
      fd.append("narration", formData.narration || "");
      fd.append("other_expense", formData.other_expense || "0.00");
      fd.append("good_items", JSON.stringify(cleanedItems));
      fd.append("terms_conditions", JSON.stringify(filteredTerms));
      fd.append("total_amount", formData.total_amount || "0.00");
      fd.append("gst_amount", formData.gst_amount || "0.00");
      fd.append("final_amount", formData.final_amount || "0.00");
      fd.append("created_by", localStorage.getItem("user_id"));

      if (isEdit && poData?.purchase_voucher_no) {
        await UpdatePurchaseVoucher(poData.purchase_voucher_no, fd);
        toast.success("Purchase Voucher updated successfully!");
      } else {
        await CreatePurchaseVoucher(fd);
        sessionStorage.removeItem("pvDraft");
        sessionStorage.removeItem("pvTerms");
        sessionStorage.removeItem("pvShipping");
        toast.success("Purchase Voucher created successfully!");
      }

      setTimeout(() => navigate("/dashboard/purchase-list"), 1500);
    } catch (err) {
      console.error("Submit error:", err);
      let errorMessage = "Failed to submit Purchase Voucher";
      if (err.response?.data) {
        if (typeof err.response.data === "object") {
          errorMessage = Object.entries(err.response.data)
            .map(
              ([field, msgs]) =>
                `${field}: ${(Array.isArray(msgs) ? msgs : [msgs]).join(", ")}`,
            )
            .join(" | ");
        } else {
          errorMessage = err.response.data;
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Draft ──
  const handleSaveDraft = () => {
    if (isEdit) return;
    sessionStorage.setItem("pvDraft", JSON.stringify(formData));
    sessionStorage.setItem("pvTerms", JSON.stringify(terms));
    sessionStorage.setItem("pvShipping", includeShipping ? "1" : "0");
    setHasDraft(true);
    toast.info("Purchase Voucher draft saved!");
  };

  const handleDeleteDraft = () => {
    sessionStorage.removeItem("pvDraft");
    sessionStorage.removeItem("pvTerms");
    sessionStorage.removeItem("pvShipping");
    setHasDraft(false);
    setIncludeShipping(false);
    setTerms(defaultTermsList);
    setSubmitErrors({});
    setItemFieldErrors([]);
    setItemOptions({});
    setRowCategoryOptions({});

    setFormData((prev) => ({
      ...prev,
      invoice_no: "",
      invoice_date: "",
      lr_no: "",
      grn_no: "",
      purchase_order: "",
      vehicle_no: "",
      payment_status: "",
      supplier: "",
      project: "",
      billing_name: "",
      billing_address: "",
      billing_pan: "",
      billing_gst: "",
      billing_email: "",
      billing_contact: "",
      billed_to_name:
        "Siddhart Industries MATERIAL HANDLING EQUIPMENTS PVT. LTD.",
      billed_to_email: "info@Siddhart Industriesmhe.com",
      billed_to_contact: "9825586999",
      billed_to_state_name: "Chattisgarh",
      billed_to_state_code: "22",
      billed_to_gst_no: "22AAECC0064F1ZC",
      billed_to_pan_no: "AAECC0064F",
      billed_to_address:
        "Shed No.6, Industrial Growth Center, Borai, Rasamada, Chhattisgarh 491001",
      shipping_address: "",
      shipping_contact: "",
      shipping_phone: "",
      narration: "",
      total_amount: "0.00",
      gst_amount: "0.00",
      other_expense: "0.00",
      final_amount: "0.00",
      items: [{ ...emptyPvItem }],
      voucher_no: prev.voucher_no,
      division: division?.division_id || "",
      created_by: localStorage.getItem("user_id"),
    }));

    toast.info("Draft deleted");
  };

  useEffect(() => {
    if (isEdit) return;
    const draftForm = sessionStorage.getItem("pvDraft");
    const draftTerms = sessionStorage.getItem("pvTerms");
    const draftShip = sessionStorage.getItem("pvShipping");
    if (!draftForm) return;
    try {
      setFormData(JSON.parse(draftForm));
      if (draftTerms) setTerms(JSON.parse(draftTerms));
      setIncludeShipping(draftShip === "1");
      setHasDraft(true);
      // Re-fetch a fresh voucher number — draft's number may be stale
      GetNextPvNo().then((data) => {
        const no = data?.purchase_number || data?.purchase_voucher_no;
        if (no) {
          setFormData((prev) => ({ ...prev, voucher_no: no, purchase_number: no }));
        }
      }).catch(() => {});
      toast.info("Loaded Purchase Voucher draft");
    } catch {}
  }, [isEdit]);


  // ════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════
  return (
    <Box
      sx={{
        px: 0,
        pt: 0,
        mt: 1,
        maxWidth: 1200,
        mx: "auto",
        bgcolor: "background.default",
        minHeight: "100%",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar
        theme={isDark ? "dark" : "light"}
      />

      {/* ── Top Buttons ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 3,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 2 : 0,
          position: "sticky",
          top: 0,
          zIndex: 1100,
          pt: 1,
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
          backdropFilter: "blur(8px)",
          boxShadow: isDark
            ? "0 2px 10px rgba(0,0,0,0.45)"
            : "0 1px 4px rgba(0,0,0,0.07)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: isMobile ? "column" : "row",
            width: isMobile ? "100%" : "auto",
          }}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={hasDraft ? handleDeleteDraft : handleSaveDraft}
            fullWidth={isMobile}
            sx={{
              height: 40,
              fontSize: "0.875rem",
              color: hasDraft ? "error.main" : "#ff8400",
              borderColor: hasDraft ? "error.main" : "#ff8400",
              fontWeight: 500,
              textTransform: "none",
              minWidth: 120,
              borderRadius: 0,
              "&:hover": {
                borderColor: hasDraft ? "error.dark" : "#e67600",
                backgroundColor: hasDraft
                  ? (t) => alpha(t.palette.error.main, 0.04)
                  : isDark
                    ? alpha("#ff8400", 0.1)
                    : "rgba(255,132,0,0.04)",
              },
            }}
          >
            {hasDraft ? "Delete Draft" : "Save Draft"}
          </Button>

          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            fullWidth={isMobile}
            sx={{
              height: 40,
              fontSize: "0.875rem",
              fontWeight: 600,
              textTransform: "none",
              minWidth: 180,
              borderRadius: 0,
            }}
          >
            {isSubmitting ? "Submitting..." : "Submit Purchase List"}
          </Button>
        </Box>
      </Box>

      {/* ── Header Form ── */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "8px",
          mb: 3,
          p: isMobile ? 1.5 : 2,
          bgcolor: "background.paper",
          backgroundImage: "none",
          boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Voucher No"
              value={formData.voucher_no || ""}
              disabled
              error={!!submitErrors.voucher_no}
              helperText={submitErrors.voucher_no}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                  borderRadius: 1,
                  bgcolor: disabledBg,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Invoice No"
              value={formData.invoice_no || ""}
              onChange={handleChange("invoice_no")}
              InputProps={{
                sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Invoice Date"
              type="date"
              value={formData.invoice_date || ""}
              onChange={handleChange("invoice_date")}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="LR No"
              value={formData.lr_no || ""}
              onChange={handleChange("lr_no")}
              InputProps={{
                sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="GRN No"
              value={formData.grn_no || ""}
              onChange={handleChange("grn_no")}
              InputProps={{
                sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Vehicle No"
              value={formData.vehicle_no || ""}
              onChange={(e) => handleFieldChange("vehicle_no", e.target.value)}
              InputProps={{
                sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Payment Status"
              value={formData.payment_status || ""}
              onChange={handleChange("payment_status")}
              InputProps={{
                sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
              }}
            >
              <MenuItem value="">Select</MenuItem>
              <MenuItem value="Paid">Paid</MenuItem>
              <MenuItem value="Unpaid">Unpaid</MenuItem>
              <MenuItem value="Partial">Partial</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={suppliers}
              getOptionLabel={(option) => option.name || ""}
              value={suppliers.find((s) => s.id === formData.supplier) || null}
              loading={supplierLoading}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              onChange={async (e, newValue) => {
                if (newValue) {
                  setFormData((prev) => ({
                    ...prev,
                    supplier: newValue.id,
                    billing_name: newValue.name || "",
                    billing_address: newValue.address || "",
                    billing_pan: newValue.pan || "",
                    billing_gst: newValue.gst || "",
                    billing_email: newValue.email || "",
                    billing_contact: newValue.contact || "",
                  }));
                  if (submitErrors.supplier)
                    setSubmitErrors((prev) => ({ ...prev, supplier: "" }));

                  try {
                    const poDataLocal = await fetchpos({
                      supplier: newValue.id,
                      approval_status: "Approved",
                    });
                    const poList = poDataLocal?.results || [];
                    if (poList.length > 0) {
                      const latestPO = poList[0];
                      const mappedItems = (latestPO.items || []).map(
                        (item) => ({
                          category: item.category
                            ? {
                                id: item.category,
                                name: item.category_name || "",
                              }
                            : null,
                          sub_category: item.sub_category
                            ? {
                                subcat_id: item.sub_category,
                                name: item.sub_category_name || "",
                              }
                            : null,
                          item: item.item
                            ? { id: item.item, name: item.item_name || "" }
                            : null,
                          unit: item.unit || "",
                          unit_name: item.unit_name || "",
                          description: item.description || item.item_name || "",
                          quantity: parseFloat(item.quantity) || 0,
                          used_qty: 0,
                          rate: parseFloat(item.rate) || 0,
                          amount:
                            (parseFloat(item.rate) || 0) *
                            (parseFloat(item.quantity) || 0),
                          size: item.size
                            ? typeof item.size === "object"
                              ? item.size
                              : { id: item.size, name: item.size_name || "" }
                            : null,
                          size_name: item.size_name || "",
                          gst_percentage: item.gst_percentage || "",
                          gst_amount: "0.00",
                        }),
                      );

                      const totals = recalcMainTotals(
                        mappedItems,
                        formData.other_expense,
                      );

                      setFormData((prev) => ({
                        ...prev,
                        billing_name:
                          latestPO.billing_name || newValue.name || "",
                        billing_address:
                          latestPO.billing_address || newValue.address || "",
                        billing_pan: latestPO.billing_pan || newValue.pan || "",
                        billing_gst: latestPO.billing_gst || newValue.gst || "",
                        billing_email:
                          latestPO.billing_email || newValue.email || "",
                        billing_contact:
                          latestPO.billing_contact || newValue.contact || "",
                        project: latestPO.project || "",
                        items:
                          mappedItems.length > 0 ? mappedItems : prev.items,
                        ...totals,
                      }));

                      if (latestPO.terms_conditions?.length > 0)
                        setTerms(latestPO.terms_conditions);

                      toast.success("Previous PO data loaded!");
                    }
                  } catch (error) {
                    console.error("Error fetching PO:", error);
                  }
                } else {
                  setFormData((prev) => ({
                    ...prev,
                    supplier: "",
                    billing_name: "",
                    billing_address: "",
                    billing_pan: "",
                    billing_gst: "",
                    billing_email: "",
                    billing_contact: "",
                  }));
                }
              }}
              onInputChange={(e, newInputValue) =>
                setSupplierSearch(newInputValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Supplier"
                  fullWidth
                  error={!!submitErrors.supplier}
                  helperText={submitErrors.supplier}
                  InputProps={{
                    ...params.InputProps,
                    sx: { height: 45, fontSize: "0.875rem", borderRadius: 1 },
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Narration"
              value={formData.narration || ""}
              onChange={handleChange("narration")}
              multiline
              rows={1}
              InputProps={{ sx: { fontSize: "0.875rem", borderRadius: 1 } }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* ── Item Errors ── */}
      {submitErrors.items && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? alpha(t.palette.warning.main, 0.15)
                : "#fff3cd",
            border: "1px solid",
            borderColor: "warning.light",
            borderRadius: 1,
          }}
        >
          <Typography color="error" variant="body2">
            {submitErrors.items}
          </Typography>
        </Box>
      )}

      {/* ── Items Table ── */}
      <TableContainer
        sx={{
          mt: 2,
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          "&::-webkit-scrollbar": { height: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: isDark ? "#555" : "#ccc",
            borderRadius: 4,
          },
        }}
      >
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
            mb: 3,
            p: 2,
            minWidth: 2200,
            bgcolor: "background.paper",
            backgroundImage: "none",
            boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
          }}
        >
          <Table sx={{ minWidth: 2050 }}>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: tableHeadBg,
                  "& th": {
                    fontWeight: "bold",
                    color: "text.primary",
                    borderBottom: "2px solid",
                    borderColor: "primary.main",
                    fontSize: "0.75rem",
                    whiteSpace: "nowrap",
                    backgroundColor: `${tableHeadBg} !important`,
                  },
                }}
              >
                <TableCell>S.No</TableCell>
                <TableCell>Product Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>UOM</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>GST %</TableCell>
                <TableCell>GST Amt</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {formData.items.map((item, index) => (
                <TableRow
                  key={index}
                  sx={{
                    bgcolor:
                      index % 2 === 0
                        ? isDark
                          ? alpha(theme.palette.common.white, 0.03)
                          : "#fafafa"
                        : "background.paper",
                    "&:hover": {
                      bgcolor: isDark
                        ? alpha(theme.palette.primary.main, 0.08)
                        : "#f0f7ff",
                    },
                    "& td": {
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    },
                  }}
                >
                  <TableCell
                    sx={{ color: "text.secondary", fontSize: "0.8rem" }}
                  >
                    {index + 1}
                  </TableCell>

                  {/* Product Type */}
                  <TableCell>
                    <Autocomplete
                      size="small"
                      options={Array.isArray(sizeOptions) ? sizeOptions : []}
                      getOptionLabel={(option) =>
                        option?.size || option?.name || ""
                      }
                      value={
                        item.size
                          ? typeof item.size === "object"
                            ? item.size
                            : sizeOptions.find(
                                (s) => String(s.id) === String(item.size),
                              ) || null
                          : null
                      }
                      onChange={(e, newValue) =>
                        handleInputChange(index, "size", newValue)
                      }
                      onInputChange={(e, newInputValue, reason) => {
                        if (reason === "input")
                          handleItemInputChange(
                            index,
                            item,
                            newInputValue,
                            "size",
                          );
                      }}
                      isOptionEqualToValue={(opt, val) =>
                        String(opt?.id) === String(val?.id)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Product Type"
                          size="small"
                          sx={{ width: "160px" }}
                        />
                      )}
                    />
                  </TableCell>

                  {/* Category */}
                  <TableCell>
                    <Autocomplete
                      options={rowCategoryOptions[index] || []}
                      getOptionLabel={(opt) => opt.name || ""}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      value={item.category || null}
                      disabled={!item.size}
                      onChange={(e, val, reason) => {
                        if (reason === "clear" || !val) {
                          handleInputChange(index, "category", null);
                          return;
                        }
                        handleInputChange(index, "category", val);
                      }}
                      onInputChange={(e, newValue, reason) => {
                        if (reason === "input")
                          handleItemInputChange(
                            index,
                            item,
                            newValue,
                            "category",
                          );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Category"
                          size="small"
                          sx={{ width: "180px" }}
                          error={!!itemFieldErrors[index]?.category}
                         
                        />
                      )}
                    />
                  </TableCell>

                  {/* Item */}
                  <TableCell>
                    <Autocomplete
                      options={
                        Array.isArray(itemOptions[index])
                          ? itemOptions[index]
                          : []
                      }
                      getOptionLabel={(opt) => opt.name || ""}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      value={item.item || null}
                      disabled={!item.category}
                      onChange={(e, val, reason) => {
                        if (reason === "clear" || !val) {
                          handleInputChange(index, "item", null);
                          return;
                        }
                        handleInputChange(index, "item", val);
                      }}
                      onInputChange={(e, newValue) =>
                        handleItemInputChange(index, item, newValue, "item")
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          sx={{ width: "180px" }}
                          label="Item"
                          error={!!itemFieldErrors[index]?.item}
                          helperText={itemFieldErrors[index]?.item}
                        />
                      )}
                    />
                  </TableCell>

                  {/* UOM */}
                  <TableCell>
                    <Autocomplete
                      size="small"
                      options={unitOptions || []}
                      getOptionLabel={(option) => {
                        if (!option) return "";
                        if (typeof option === "object")
                          return option?.name || option?.unit_name || "";
                        const found = unitOptions.find(
                          (u) => String(u.id) === String(option),
                        );
                        return found?.name || "";
                      }}
                      value={
                        item.unit
                          ? unitOptions.find(
                              (u) => String(u.id) === String(item.unit),
                            ) || null
                          : null
                      }
                      onChange={(e, newValue) =>
                        handleInputChange(index, "unit", newValue)
                      }
                      isOptionEqualToValue={(option, value) => {
                        if (!option && !value) return true;
                        if (!option || !value) return false;
                        return String(option.id) === String(value.id);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Unit"
                          size="small"
                          sx={{ width: "150px" }}
                          error={!!itemFieldErrors[index]?.unit}
                          helperText={itemFieldErrors[index]?.unit}
                        />
                      )}
                    />
                  </TableCell>

                  {/* Quantity */}
                  <TableCell>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) =>
                        handleItemChange(index, "quantity", e.target.value)
                      }
                      onFocus={(e) => {
                        if (e.target.value === "0") e.target.value = "";
                      }}
                      onBlur={(e) =>
                        handleItemChange(
                          index,
                          "quantity",
                          (parseFloat(e.target.value) || 0).toString(),
                        )
                      }
                      style={{
                        ...nativeInputStyle(!!itemFieldErrors[index]?.quantity),
                        width: "150px",
                      }}
                    />
                    {itemFieldErrors[index]?.quantity && (
                      <div style={{ color: "red", fontSize: "0.8rem" }}>
                        {itemFieldErrors[index]?.quantity}
                      </div>
                    )}
                  </TableCell>

                  {/* Rate */}
                  <TableCell>
                    <input
                      type="number"
                      value={item.rate}
                      onChange={(e) =>
                        handleItemChange(index, "rate", e.target.value)
                      }
                      onBlur={(e) =>
                        handleItemChange(
                          index,
                          "rate",
                          (parseFloat(e.target.value) || 0).toString(),
                        )
                      }
                      style={{
                        ...nativeInputStyle(!!itemFieldErrors[index]?.rate),
                        width: "150px",
                      }}
                    />
                    {itemFieldErrors[index]?.rate && (
                      <div style={{ color: "red", fontSize: "0.8rem" }}>
                        {itemFieldErrors[index]?.rate}
                      </div>
                    )}
                  </TableCell>

                  {/* Amount */}
                  <TableCell>
                    <input
                      type="number"
                      value={item.amount}
                      readOnly
                      style={{ ...nativeInputStyle(), width: "150px" }}
                    />
                  </TableCell>

                  {/* GST % */}
                  <TableCell>
                    <input
                      type="number"
                      value={item.gst_percentage}
                      onChange={(e) =>
                        handleItemChange(
                          index,
                          "gst_percentage",
                          e.target.value,
                        )
                      }
                      onBlur={(e) =>
                        handleItemChange(
                          index,
                          "gst_percentage",
                          (parseFloat(e.target.value) || 0).toString(),
                        )
                      }
                      style={{ ...nativeInputStyle(), width: "100px" }}
                    />
                  </TableCell>

                  {/* GST Amount */}
                  <TableCell>
                    <input
                      type="number"
                      value={item.gst_amount}
                      readOnly
                      style={{ ...nativeInputStyle(), width: "120px" }}
                    />
                  </TableCell>

                  {/* Total */}
                  <TableCell>
                    <input
                      type="text"
                      readOnly
                      value={(
                        (parseFloat(item.amount) || 0) +
                        (parseFloat(item.gst_amount) || 0)
                      ).toFixed(2)}
                      style={{ ...nativeInputStyle(), width: "150px" }}
                    />
                  </TableCell>

                  {/* Description */}
                  <TableCell>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) =>
                        handleItemChange(index, "description", e.target.value)
                      }
                      style={{ ...nativeInputStyle(), width: "150px" }}
                    />
                  </TableCell>

                  {/* Delete */}
                  <TableCell>
                    <IconButton
                      onClick={() => deleteItemRow(index)}
                      color="error"
                      sx={{
                        "&:hover": {
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? alpha(t.palette.error.main, 0.15)
                              : "#ffebee",
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </TableContainer>

      {/* ── Add Item + Totals ── */}
      <TableContainer>
        <Box sx={{ mb: 3, width: "100%" }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell colSpan={13} align="center">
                  <Button
                    variant="outlined"
                    onClick={addItemRow}
                    startIcon={<AddIcon />}
                  >
                    Add Item
                  </Button>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell colSpan={13}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "wrap",
                      p: 1,
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha(t.palette.primary.main, 0.08)
                          : alpha(t.palette.primary.main, 0.03),
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      sx={{ fontWeight: "bold", color: "text.primary" }}
                    >
                      Total Amount:
                    </Typography>
                    <TextField
                      value={formData.total_amount}
                      size="small"
                      sx={{
                        width: "120px",
                        "& .MuiInputBase-root": { bgcolor: disabledBg },
                      }}
                      disabled
                    />

                    <Typography
                      sx={{ fontWeight: "bold", color: "text.primary" }}
                    >
                      Total GST:
                    </Typography>
                    <TextField
                      value={formData.gst_amount}
                      size="small"
                      sx={{
                        width: "120px",
                        "& .MuiInputBase-root": { bgcolor: disabledBg },
                      }}
                      disabled
                    />

                    <Typography
                      sx={{ fontWeight: "bold", color: "text.primary" }}
                    >
                      Other Expense:
                    </Typography>
                    <TextField
                      type="number"
                      value={formData.other_expense}
                      onChange={(e) => handleOtherExpenseChange(e.target.value)}
                      size="small"
                      sx={{ width: "120px" }}
                    />

                    <Typography
                      sx={{ fontWeight: "bold", color: "success.main" }}
                    >
                      Grand Total:
                    </Typography>
                    <TextField
                      value={formData.final_amount}
                      size="small"
                      sx={{
                        width: "120px",
                        "& .MuiInputBase-root": {
                          bgcolor: isDark
                            ? alpha(theme.palette.success.main, 0.12)
                            : "#e8f5e9",
                          fontWeight: "bold",
                        },
                      }}
                      disabled
                    />
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </TableContainer>
    </Box>
  );
}
